import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Download, Wine, Lock, Globe, MessageSquare, Calendar, 
  Users, TrendingUp, CheckCircle, Smartphone, BarChart, 
  Shield, Clock, Zap, Home, ArrowLeft
} from 'lucide-react';
import html2pdf from 'html2pdf.js';

const ProposalView = () => {
  const navigate = useNavigate();
  const proposalRef = useRef(null);

  useEffect(() => {
    // Verificar código de acceso
    const code = sessionStorage.getItem('proposal_code');
    if (!code) {
      navigate('/propuesta');
    }
  }, [navigate]);

  const downloadPDF = () => {
    const element = proposalRef.current;
    const opt = {
      margin: 10,
      filename: 'Propuesta_Bodega_Spinoglio.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_wine-portal/artifacts/y2ch10fe_Logo_Artificialmente_Para_Modo_Claro.png" 
              alt="Artificialmente Logo" 
              className="h-8 md:h-10 w-auto flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-sm md:text-xl font-bold text-gray-800 truncate">Bodega Spinoglio</h1>
              <p className="text-xs text-gray-600 hidden sm:block">Propuesta Comercial</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 md:py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-all text-xs md:text-sm flex-shrink-0"
            >
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Volver al sitio</span>
            </button>
            <button
              onClick={downloadPDF}
              className="flex items-center gap-1 md:gap-2 px-3 md:px-6 py-2 md:py-3 text-white rounded-lg font-semibold transition-all hover:scale-105 shadow-lg text-xs md:text-base flex-shrink-0"
              style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
            >
              <Download className="h-4 w-4 md:h-5 md:w-5" />
              <span className="hidden sm:inline">Descargar PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-24 pb-12">
        <div ref={proposalRef} className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl overflow-hidden">
          
          {/* Hero Section */}
          <div className="relative min-h-[350px] md:h-96 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white p-6 md:p-12 flex flex-col justify-center">
            <div className="absolute inset-0 opacity-10"
                 style={{
                   backgroundImage: 'url(https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1200)',
                   backgroundSize: 'cover',
                   backgroundPosition: 'center'
                 }}
            />
            <div className="relative z-10">
              <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                <Wine className="h-10 w-10 md:h-16 md:w-16 flex-shrink-0" style={{color: '#D4AF37'}} />
                <div>
                  <h1 className="text-2xl md:text-5xl font-bold mb-1 md:mb-2">Bodega Spinoglio</h1>
                  <p className="text-sm md:text-2xl" style={{color: '#D4AF37'}}>Sistema Integral de Gestión</p>
                </div>
              </div>
              <p className="text-sm md:text-xl text-gray-300 max-w-2xl mb-4 md:mb-0">
                Transforme su bodega con tecnología de vanguardia: Sitio web profesional + Asistente IA + Panel de administración completo
              </p>
              <div className="mt-4 md:mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4">
                <div className="px-3 md:px-4 py-2 bg-white/10 backdrop-blur rounded-lg w-full sm:w-auto">
                  <p className="text-xs md:text-sm text-gray-300">Fecha</p>
                  <p className="font-semibold text-sm md:text-base">{new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
                <div className="px-3 md:px-4 py-2 bg-white/10 backdrop-blur rounded-lg w-full sm:w-auto">
                  <p className="text-xs md:text-sm text-gray-300">Confidencial</p>
                  <p className="font-semibold text-sm md:text-base flex items-center gap-2">
                    <Lock className="h-3 w-3 md:h-4 md:w-4" />
                    Propuesta Exclusiva
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Resumen Ejecutivo */}
          <div className="p-12 border-b border-gray-200">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Resumen Ejecutivo</h2>
            
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl mb-8 border-l-4" style={{borderColor: '#D4AF37'}}>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Presentamos una <strong>solución integral y escalable</strong> diseñada específicamente para bodegas modernas que buscan digitalizar su operación, mejorar la experiencia del cliente y optimizar la gestión administrativa.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Nuestra propuesta combina tres pilares fundamentales:</strong>
              </p>
              <ul className="space-y-3 text-gray-700 ml-4">
                <li className="flex items-start gap-3">
                  <span className="text-2xl font-bold" style={{color: '#D4AF37'}}>1.</span>
                  <div>
                    <strong>Presencia Digital Profesional:</strong> Un sitio web moderno y responsive que refleja la calidad y elegancia de su bodega, con sistema de reservas integrado y optimizado para motores de búsqueda.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl font-bold" style={{color: '#D4AF37'}}>2.</span>
                  <div>
                    <strong>Asistente Virtual Inteligente:</strong> Atención automatizada 24/7 a través de WhatsApp y web chat, capaz de responder consultas, recomendar experiencias y gestionar reservas sin intervención humana.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl font-bold" style={{color: '#D4AF37'}}>3.</span>
                  <div>
                    <strong>Gestión Administrativa Completa:</strong> Panel centralizado para administrar contenido web, conversaciones con clientes, reservas, inventario, ventas, proveedores y contabilidad básica.
                  </div>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900">
                  <strong>💡 Valor diferencial:</strong> Esta solución no solo mejora la presencia online, sino que <strong>automatiza procesos</strong>, <strong>libera tiempo</strong> de su equipo y proporciona <strong>datos valiosos</strong> para tomar decisiones estratégicas.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-800 mb-6">Componentes Principales</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl" style={{background: 'rgba(212, 175, 55, 0.05)'}}>
                <Globe className="h-12 w-12 mb-4" style={{color: '#D4AF37'}} />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Sitio Web Premium</h3>
                <p className="text-gray-600">Diseño elegante y responsive que refleja la calidad y experiencia de la bodega</p>
              </div>
              <div className="p-6 bg-blue-50 rounded-xl">
                <MessageSquare className="h-12 w-12 mb-4 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Asistente IA 24/7</h3>
                <p className="text-gray-600">Atención automática que responde consultas y toma reservas</p>
              </div>
              <div className="p-6 bg-green-50 rounded-xl">
                <BarChart className="h-12 w-12 mb-4 text-green-600" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Panel Admin</h3>
                <p className="text-gray-600">Control total de clientes, reservas, conversaciones + web</p>
              </div>
            </div>
          </div>

          {/* Componentes del Sistema */}
          <div className="p-12 bg-gray-50">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Componentes del Sistema</h2>
            
            {/* Tres columnas en desktop, stack en móvil */}
            <div className="grid md:grid-cols-3 gap-8">
              
              {/* Sitio Web */}
              <div className="flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-lg" style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}>
                    <Globe className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Sitio Web</h3>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm flex-1">
                  <h4 className="font-bold text-gray-800 mb-3">Secciones:</h4>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                      <span>Hero con Carousel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                      <span>Tours y Experiencias</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                      <span>Restaurant</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                      <span>Catálogo de Vinos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                      <span>Reservas Online con pagos</span>
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-bold text-gray-800 mb-2 text-sm">Características:</h4>
                    <ul className="space-y-2 text-xs text-gray-600">
                      <li className="flex items-center gap-2">
                        <Smartphone className="h-3 w-3" style={{color: '#D4AF37'}} />
                        Responsive
                      </li>
                      <li className="flex items-center gap-2">
                        <Zap className="h-3 w-3" style={{color: '#D4AF37'}} />
                        Carga rápida
                      </li>
                      <li className="flex items-center gap-2">
                        <Shield className="h-3 w-3" style={{color: '#D4AF37'}} />
                        SSL incluido
                      </li>
                      <li className="flex items-center gap-2">
                        <Globe className="h-3 w-3" style={{color: '#D4AF37'}} />
                        SEO optimizado
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Asistente IA */}
              <div className="flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-blue-600">
                    <MessageSquare className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Asistente IA</h3>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm flex-1">
                  <h4 className="font-bold text-gray-800 mb-3">Capacidades:</h4>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                      <span>Responde consultas sobre vinos, tours, horarios</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                      <span>Toma reservas automáticamente</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                      <span>Recomendaciones personalizadas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                      <span>WhatsApp, Web Chat, RRSS</span>
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-bold text-gray-800 mb-2 text-sm">Control:</h4>
                    <ul className="space-y-2 text-xs text-gray-600">
                      <li className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-blue-600" />
                        Disponible 24/7
                      </li>
                      <li className="flex items-center gap-2">
                        <MessageSquare className="h-3 w-3 text-blue-600" />
                        Modo manual
                      </li>
                      <li className="flex items-center gap-2">
                        <BarChart className="h-3 w-3 text-blue-600" />
                        Reportes y métricas
                      </li>
                      <li className="flex items-center gap-2">
                        <Zap className="h-3 w-3 text-blue-600" />
                        Respuestas instantáneas
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Panel de Administración */}
              <div className="flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-green-600">
                    <BarChart className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Panel Admin</h3>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm flex-1">
                  <h4 className="font-bold text-gray-800 mb-3">Gestión IA:</h4>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                      <span>Dashboard con métricas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                      <span>Monitoreo de conversaciones</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                      <span>Base de datos de clientes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                      <span>Vista calendario reservas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                      <span>Configuración de Prompts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                      <span>Mapa geolocalizado</span>
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-bold text-gray-800 mb-2 text-sm">Gestión Web:</h4>
                    <ul className="space-y-2 text-xs text-gray-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 flex-shrink-0 text-green-600" />
                        <span>CRUD de Noticias</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 flex-shrink-0 text-green-600" />
                        <span>Configuración del sitio</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 flex-shrink-0 text-green-600" />
                        <span>Gestión de Galería</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Beneficios */}
          <div className="p-12 border-b border-gray-200">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Beneficios para su Bodega</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center"
                       style={{background: 'rgba(212, 175, 55, 0.1)'}}>
                    <TrendingUp className="h-6 w-6" style={{color: '#D4AF37'}} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Aumento de Reservas</h3>
                  <p className="text-gray-600">Disponibilidad 24/7 significa más oportunidades de venta. El asistente nunca duerme.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-50">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Ahorro de Tiempo</h3>
                  <p className="text-gray-600">Automatice respuestas repetitivas. Su equipo se enfoca en lo importante.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-green-50">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Mejor Experiencia del Cliente</h3>
                  <p className="text-gray-600">Respuestas instantáneas, proceso de reserva fluido, información siempre actualizada.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-purple-50">
                    <BarChart className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Datos e Insights</h3>
                  <p className="text-gray-600">Comprenda a sus clientes. Métricas detalladas para tomar mejores decisiones.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-orange-50">
                    <Globe className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Presencia Digital Premium</h3>
                  <p className="text-gray-600">Sitio web que refleja la calidad de sus vinos. Primeras impresiones memorables.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-red-50">
                    <Shield className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Tecnología Confiable</h3>
                  <p className="text-gray-600">Sistema robusto, seguro y escalable. Soporte técnico incluido.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Especificaciones Técnicas */}
          <div className="p-12 border-b border-gray-200">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Especificaciones Técnicas</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Stack Tecnológico */}
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{background: 'rgba(212, 175, 55, 0.1)'}}>
                    <Zap className="h-5 w-5" style={{color: '#D4AF37'}} />
                  </div>
                  Stack Tecnológico
                </h3>
                <div className="space-y-3">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Frontend</p>
                    <p className="text-sm text-gray-600">React.js + TailwindCSS</p>
                    <p className="text-xs text-gray-500 mt-1">Interfaz moderna, rápida y responsive</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Backend</p>
                    <p className="text-sm text-gray-600">Python + FastAPI</p>
                    <p className="text-xs text-gray-500 mt-1">API REST de alto rendimiento</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Base de Datos</p>
                    <p className="text-sm text-gray-600">MongoDB</p>
                    <p className="text-xs text-gray-500 mt-1">Escalable y flexible</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{background: 'rgba(212, 175, 55, 0.05)'}}>
                    <p className="font-semibold text-gray-800 mb-2">Inteligencia Artificial</p>
                    <p className="text-sm text-gray-600">GPT-4 + APIs especializadas</p>
                    <p className="text-xs text-gray-500 mt-1">Respuestas naturales y contextuales</p>
                  </div>
                </div>
              </div>

              {/* Infraestructura y Seguridad */}
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                  Infraestructura y Seguridad
                </h3>
                <div className="space-y-3">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Hosting</p>
                    <p className="text-sm text-gray-600">Cloud de alta disponibilidad</p>
                    <p className="text-xs text-gray-500 mt-1">99.9% uptime garantizado</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Seguridad</p>
                    <p className="text-sm text-gray-600">SSL/TLS + Autenticación JWT</p>
                    <p className="text-xs text-gray-500 mt-1">Datos encriptados y protegidos</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Backups</p>
                    <p className="text-sm text-gray-600">Automáticos diarios</p>
                    <p className="text-xs text-gray-500 mt-1">Recuperación ante desastres</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Monitoreo</p>
                    <p className="text-sm text-gray-600">24/7 proactivo</p>
                    <p className="text-xs text-gray-500 mt-1">Detección temprana de problemas</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="p-6 bg-gradient-to-br from-green-50 to-white rounded-xl border border-green-200">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold text-gray-800">99.9%</p>
                    <p className="text-sm text-gray-600">Disponibilidad</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600">Sistema operativo 24/7</p>
              </div>

              <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-200">
                <div className="flex items-center gap-3 mb-3">
                  <Zap className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold text-gray-800">&lt;2s</p>
                    <p className="text-sm text-gray-600">Tiempo de Carga</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600">Optimizado para velocidad</p>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-200">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="text-2xl font-bold text-gray-800">100%</p>
                    <p className="text-sm text-gray-600">Seguro</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600">Certificado SSL incluido</p>
              </div>
            </div>
          </div>

          {/* Inversión - Tres Opciones */}
          <div className="p-12 bg-gradient-to-br from-gray-50 to-white border-b border-gray-200">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">Inversión</h2>
            <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
              Ofrecemos tres opciones flexibles para adaptarnos a sus necesidades y presupuesto
            </p>
            
            <div className="max-w-7xl mx-auto grid lg:grid-cols-3 md:grid-cols-2 gap-6">
              
              {/* Opción 1: Solo IA + Admin */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-blue-300 relative">
                <div className="absolute top-4 right-4">
                  <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">OPCIÓN 1</span>
                </div>
                <div className="h-[160px] p-6 text-center bg-gradient-to-br from-blue-500 to-blue-600 flex flex-col justify-center items-center">
                  <h3 className="text-2xl font-bold text-white mb-2">Asistente IA + Admin</h3>
                  <p className="text-white/90 text-sm">Ideal para bodegas con sitio web existente</p>
                </div>
                
                <div className="p-8">
                  <div className="mb-6">
                    <div className="text-center p-6 bg-blue-50 rounded-xl mb-4">
                      <p className="text-sm text-gray-600 mb-2">Inversión Inicial</p>
                      <p className="text-2xl font-bold text-gray-800 mb-1">2 pagos de USD 1.710</p>
                      <p className="text-xs text-gray-500">Al comenzar + Al entregar</p>
                    </div>
                    <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl">
                      <p className="text-sm text-gray-600 mb-2">Mensualidad</p>
                      <p className="text-4xl font-bold text-gray-800 mb-2">USD 287</p>
                      <p className="text-sm text-gray-600">Por mes</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-bold text-gray-800 text-sm mb-3">✨ Qué incluye:</h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                        <span><strong>Asistente IA 24/7</strong> WhatsApp + Web Chat</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                        <span><strong>Panel de administración completo</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                        <span>Gestión de conversaciones y clientes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                        <span>Calendario de reservas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                        <span>Mapa geolocalizado</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                        <span>Dashboard con métricas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                        <span>Soporte técnico prioritario</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                        <span>Actualizaciones del sistema y LLMs</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Opción 2: Combo Completo */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 relative" style={{borderColor: '#D4AF37'}}>
                <div className="absolute top-4 right-4">
                  <span className="text-white text-xs font-bold px-3 py-1 rounded-full" style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}>
                    OPCIÓN 2
                  </span>
                </div>
                <div className="h-[160px] p-6 text-center flex flex-col justify-center items-center" style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}>
                  <h3 className="text-2xl font-bold text-white mb-2">IA + Web Completa</h3>
                  <p className="text-white/90 text-sm">Presencia digital profesional</p>
                </div>
                
                <div className="p-8">
                  <div className="mb-6">
                    <div className="text-center p-6 rounded-xl mb-4" style={{background: 'rgba(212, 175, 55, 0.1)'}}>
                      <p className="text-sm text-gray-600 mb-2">Inversión Inicial</p>
                      <p className="text-2xl font-bold text-gray-800 mb-1">2 pagos de USD 2.270</p>
                      <p className="text-xs text-gray-500">Al comenzar + Al entregar</p>
                    </div>
                    <div className="text-center p-6 rounded-xl" style={{background: 'rgba(212, 175, 55, 0.05)'}}>
                      <p className="text-sm text-gray-600 mb-2">Mensualidad</p>
                      <p className="text-4xl font-bold text-gray-800 mb-2">USD 340</p>
                      <p className="text-sm text-gray-600">Por mes</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-bold text-gray-800 text-sm mb-3">🚀 Todo lo de Opción 1 +</h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                        <span><strong>Sitio web premium completo</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                        <span>Diseño responsive y optimizado</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                        <span>Hero carousel con imágenes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                        <span>Secciones: Tours, Restaurant, Vinos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                        <span>Sistema de reservas con pagos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                        <span><strong>Admin de contenido web</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                        <span>Gestión de noticias y galería</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                        <span>SEO optimizado</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                        <span>SSL y hosting incluidos</span>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-6 p-4 rounded-lg" style={{background: 'rgba(212, 175, 55, 0.1)'}}>
                    <p className="text-xs text-gray-700 text-center">
                      <strong>💎 Solución completa:</strong> Web profesional integrada con IA
                    </p>
                  </div>
                </div>
              </div>

              {/* Opción 3: Suite Empresarial */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-green-400 relative">
                <div className="absolute top-4 right-4">
                  <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    OPCIÓN 3 - MÁS COMPLETA
                  </span>
                </div>
                <div className="h-[160px] p-6 text-center bg-gradient-to-br from-green-600 to-green-700 flex flex-col justify-center items-center">
                  <h3 className="text-2xl font-bold text-white mb-2">Suite Empresarial</h3>
                  <p className="text-white/90 text-sm">Gestión administrativa completa</p>
                </div>
                
                <div className="p-8">
                  <div className="mb-6">
                    <div className="text-center p-6 bg-green-50 rounded-xl mb-4">
                      <p className="text-sm text-gray-600 mb-2">Inversión Inicial</p>
                      <p className="text-2xl font-bold text-gray-800 mb-1">2 pagos de USD 2.710</p>
                      <p className="text-xs text-gray-500">Al comenzar + Al entregar</p>
                    </div>
                    <div className="text-center p-6 bg-gradient-to-br from-green-50 to-white rounded-xl">
                      <p className="text-sm text-gray-600 mb-2">Mensualidad</p>
                      <p className="text-4xl font-bold text-gray-800 mb-2">USD 410</p>
                      <p className="text-sm text-gray-600">Por mes</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-bold text-gray-800 text-sm mb-3">⭐ Todo lo de Opción 2 +</h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span><strong>Módulo de Stock completo</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span>Gestión de inventario con alertas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span>Carga manual y desde Excel</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span><strong>Registro de ventas</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span>Seguimiento detallado de transacciones</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span><strong>Gestión de proveedores</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span>Base de datos de contactos y pagos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span><strong>Contabilidad básica</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span>Movimientos de caja y transferencias</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                        <span>Reportes financieros y exportación</span>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs text-green-800 text-center">
                      <strong>🏆 Suite completa:</strong> Control total de tu negocio desde un solo lugar
                    </p>
                  </div>
                </div>
              </div>

            </div>

            <div className="mt-10 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center max-w-4xl mx-auto">
              <p className="text-sm text-blue-800">
                <strong>💡 Nota:</strong> La mensualidad garantiza servicios cloud, hosting, microservicios IA, actualizaciones del sistema y LLMs, soporte técnico prioritario y monitoreo 24/7. Su sistema siempre actualizado, seguro y funcionando al máximo rendimiento.
              </p>
            </div>

            {/* Condiciones Adicionales */}
            <div className="mt-8 max-w-4xl mx-auto bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Condiciones y Aclaraciones:</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <span>La forma de pago se acuerda con el cliente (dependiendo del tipo de modalidad elegida).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <span>La plataforma admite ampliaciones opcionales a futuro (nuevas unidades y funcionalidades) que, si las requieren, pueden fortalecer la organización y la productividad en la empresa.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <span>Los costos expresados en esta propuesta no incluyen ningún tipo de impuesto.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">•</span>
                  <span>Este presupuesto no incluye viáticos, ni sesión de fotos, ni ningún servicio que no esté expresado en este documento.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-900 text-white p-12">
            <div className="text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_wine-portal/artifacts/2nxu7174_dj0phkic_Logo_Artificialmente_S_Fondo_Blanco.png" 
                alt="Artificialmente Logo" 
                className="h-16 w-auto mx-auto mb-6 brightness-0 invert"
              />
              <h2 className="text-2xl font-bold mb-8">¿Listo para Transformar su Bodega?</h2>
              
              <div className="max-w-xl mx-auto p-6 bg-white/5 rounded-xl backdrop-blur">
                <p className="text-sm text-gray-400 mb-4">Propuesta presentada por:</p>
                <h3 className="text-2xl font-bold mb-4" style={{color: '#D4AF37'}}>Artificialmente</h3>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">
                    <strong>Email:</strong> info@artificialmente.uy
                  </p>
                  <p className="text-gray-300">
                    <strong>Teléfono:</strong> +598 97 515 555
                  </p>
                  <p className="text-gray-300">
                    <strong>Web:</strong> artificialmente.uy
                  </p>
                </div>
              </div>

              <div className="pt-8 mt-8 border-t border-gray-700">
                <p className="text-sm text-gray-400">
                  © 2025 Artificialmente. Esta propuesta es confidencial y de uso exclusivo para Bodega Spinoglio.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProposalView;
